import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {calculateStorageUsage, loadSettings, saveSettings as saveSettingsUtil} from '../utils/adminSettings';

const {FiSettings, FiSave, FiMail, FiGlobe, FiDatabase, FiTrash2, FiDownload, FiBarChart3} = FiIcons;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    siteTitle: 'aiximo',
    siteDescription: 'AI-powered software development agency',
    contactEmail: 'info@aiximo.com',
    enableNotifications: true,
    autoBackup: true,
    maintenanceMode: false
  });

  const [stats, setStats] = useState({
    totalPosts: JSON.parse(localStorage.getItem('blog_posts') || '[]').length,
    totalMessages: JSON.parse(localStorage.getItem('contact_messages') || '[]').length,
    storageUsed: calculateStorageUsage()
  });

  useEffect(() => {
    const loadedSettings = loadSettings();
    setSettings(loadedSettings);
  }, []);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({...prev, [key]: value}));
  };

  const handleSaveSettings = () => {
    const result = saveSettingsUtil(settings);
    if (result.success) {
      alert('Settings saved successfully!');
    } else {
      alert('Failed to save settings: ' + result.error);
    }
  };

  const exportData = () => {
    const data = {
      posts: JSON.parse(localStorage.getItem('blog_posts') || '[]'),
      messages: JSON.parse(localStorage.getItem('contact_messages') || '[]'),
      settings: settings,
      exportDate: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aiximo-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      if (window.confirm('This will delete all blog posts and messages. Are you absolutely sure?')) {
        localStorage.removeItem('blog_posts');
        localStorage.removeItem('contact_messages');
        localStorage.removeItem('admin_settings');
        alert('All data cleared successfully!');
        window.location.reload();
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 text-sm">Manage your site configuration and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiSettings} className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">General Settings</h2>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Site Title</label>
              <input
                type="text"
                value={settings.siteTitle}
                onChange={(e) => handleSettingChange('siteTitle', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Site Description</label>
              <textarea
                value={settings.siteDescription}
                onChange={(e) => handleSettingChange('siteDescription', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
              <input
                type="email"
                value={settings.contactEmail}
                onChange={(e) => handleSettingChange('contactEmail', e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                  <p className="text-xs text-gray-500">Receive notifications for new messages</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.enableNotifications}
                  onChange={(e) => handleSettingChange('enableNotifications', e.target.checked)}
                  className="h-4 w-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Auto Backup</label>
                  <p className="text-xs text-gray-500">Automatically backup data weekly</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.autoBackup}
                  onChange={(e) => handleSettingChange('autoBackup', e.target.checked)}
                  className="h-4 w-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Maintenance Mode</label>
                  <p className="text-xs text-gray-500">Hide site from public view</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.maintenanceMode}
                  onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
                  className="h-4 w-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
              </div>
            </div>

            <button
              onClick={handleSaveSettings}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiSave} className="h-4 w-4" />
              <span>Save Settings</span>
            </button>
          </div>
        </div>

        {/* Data Management */}
        <div className="space-y-6">
          {/* Statistics */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiBarChart3} className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Data Overview</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg text-center border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{stats.totalPosts}</p>
                <p className="text-sm text-gray-600">Blog Posts</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{stats.totalMessages}</p>
                <p className="text-sm text-gray-600">Messages</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center col-span-2 border border-gray-200">
                <p className="text-lg font-bold text-gray-900">{stats.storageUsed} KB</p>
                <p className="text-sm text-gray-600">Storage Used</p>
              </div>
            </div>
          </div>

          {/* Data Actions */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Data Management</h2>
            <div className="space-y-3">
              <button
                onClick={exportData}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiDownload} className="h-4 w-4" />
                <span>Export All Data</span>
              </button>

              <button
                onClick={clearAllData}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                <span>Clear All Data</span>
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h2>
            <div className="space-y-3">
              <a
                href="mailto:info@aiximo.com"
                className="w-full bg-blue-50 hover:bg-blue-100 text-blue-600 py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2 border border-blue-200"
              >
                <SafeIcon icon={FiMail} className="h-4 w-4" />
                <span>Open Email</span>
              </a>

              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-purple-50 hover:bg-purple-100 text-purple-600 py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2 border border-purple-200"
              >
                <SafeIcon icon={FiGlobe} className="h-4 w-4" />
                <span>View Site</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;